import { useEffect, useState } from 'react';
import { 
  Package, AlertTriangle, Plus, Minus, Search, Filter,
  RefreshCw, History, CheckCircle, XCircle, Edit2
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminInventory = () => {
  const [inventory, setInventory] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [adjustmentData, setAdjustmentData] = useState({
    variant_index: 0,
    quantity: 1,
    action: 'add',
    reason: '',
    notes: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      const headers = { Authorization: `Bearer ${token}` };

      const [invRes, alertsRes, historyRes] = await Promise.all([
        axios.get(`${API}/admin/inventory`, { headers }),
        axios.get(`${API}/admin/inventory/alerts`, { headers }),
        axios.get(`${API}/admin/inventory/history?limit=20`, { headers })
      ]);

      setInventory(invRes.data);
      setAlerts(alertsRes.data);
      setHistory(historyRes.data.history || []);
    } catch (error) {
      console.error('Error loading inventory:', error);
      toast.error('Failed to load inventory data');
    } finally {
      setLoading(false);
    }
  };

  const handleAdjustStock = async () => {
    if (!selectedProduct || !adjustmentData.reason) {
      toast.error('Please select a reason for adjustment');
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(`${API}/admin/inventory/adjust`, {
        product_id: selectedProduct.id,
        ...adjustmentData
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Stock adjusted successfully');
      setShowAdjustModal(false);
      setSelectedProduct(null);
      loadData();
    } catch (error) {
      console.error('Error adjusting stock:', error);
      toast.error(error.response?.data?.detail || 'Failed to adjust stock');
    }
  };

  const openAdjustModal = (product, variantIndex = 0) => {
    setSelectedProduct(product);
    setAdjustmentData({
      variant_index: variantIndex,
      quantity: 1,
      action: 'add',
      reason: '',
      notes: ''
    });
    setShowAdjustModal(true);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const getStockBadge = (stock, threshold = 5) => {
    if (stock === 0) {
      return <span className="px-2 py-0.5 text-xs bg-red-100 text-red-700">Out of Stock</span>;
    } else if (stock <= threshold) {
      return <span className="px-2 py-0.5 text-xs bg-orange-100 text-orange-700">Low Stock ({stock})</span>;
    }
    return <span className="px-2 py-0.5 text-xs bg-green-100 text-green-700">In Stock ({stock})</span>;
  };

  const filteredProducts = inventory?.products?.filter(product => {
    const matchesSearch = product.name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (filterType === 'all') return matchesSearch;
    if (filterType === 'out_of_stock') return matchesSearch && product.out_of_stock > 0;
    if (filterType === 'low_stock') return matchesSearch && product.low_stock > 0;
    if (filterType === 'in_stock') return matchesSearch && product.out_of_stock === 0 && product.low_stock === 0;
    
    return matchesSearch;
  }) || [];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-inventory">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-light text-charcoal flex items-center gap-2">
            <Package className="w-6 h-6 text-gold" />
            Inventory Management
          </h1>
          <p className="text-charcoal-light mt-1">Track and manage your stock levels</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowHistoryModal(true)}
            className="flex items-center gap-2 px-4 py-2 border border-charcoal/20 text-charcoal hover:bg-beige"
          >
            <History className="w-4 h-4" /> History
          </button>
          <button
            onClick={loadData}
            className="flex items-center gap-2 px-4 py-2 bg-gold text-white hover:bg-gold-dark"
          >
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-white border border-charcoal/10 p-4">
          <p className="text-2xl font-heading font-light text-charcoal">
            {inventory?.summary?.total_products || 0}
          </p>
          <p className="text-xs text-charcoal-light">Total Products</p>
        </div>
        <div className="bg-white border border-charcoal/10 p-4">
          <p className="text-2xl font-heading font-light text-charcoal">
            {inventory?.summary?.total_units || 0}
          </p>
          <p className="text-xs text-charcoal-light">Total Units</p>
        </div>
        <div className="bg-white border border-charcoal/10 p-4">
          <p className="text-2xl font-heading font-light text-gold">
            {formatCurrency(inventory?.summary?.total_stock_value)}
          </p>
          <p className="text-xs text-charcoal-light">Stock Value</p>
        </div>
        <div className="bg-white border border-charcoal/10 p-4">
          <p className="text-2xl font-heading font-light text-orange-600">
            {inventory?.summary?.low_stock_variants || 0}
          </p>
          <p className="text-xs text-charcoal-light">Low Stock Variants</p>
        </div>
        <div className="bg-white border border-charcoal/10 p-4">
          <p className="text-2xl font-heading font-light text-red-600">
            {inventory?.summary?.out_of_stock_variants || 0}
          </p>
          <p className="text-xs text-charcoal-light">Out of Stock</p>
        </div>
      </div>

      {/* Alerts Section */}
      {alerts?.total_alerts > 0 && (
        <div className="bg-orange-50 border border-orange-200 p-4">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-5 h-5 text-orange-600" />
            <h3 className="font-medium text-orange-800">
              {alerts.total_alerts} Stock Alerts
              <span className="text-sm font-normal ml-2">
                ({alerts.critical} critical, {alerts.warning} warnings)
              </span>
            </h3>
          </div>
          <div className="flex flex-wrap gap-2">
            {alerts.alerts?.slice(0, 8).map((alert, idx) => (
              <button
                key={idx}
                onClick={() => {
                  const product = inventory?.products?.find(p => p.id === alert.product_id);
                  if (product) openAdjustModal(product, alert.variant_index);
                }}
                className={`flex items-center gap-2 px-3 py-1.5 text-sm ${
                  alert.severity === 'critical' 
                    ? 'bg-red-100 text-red-800 hover:bg-red-200' 
                    : 'bg-orange-100 text-orange-800 hover:bg-orange-200'
                } transition-colors`}
              >
                <span className="truncate max-w-32">{alert.product_name}</span>
                <span className="text-xs opacity-75">{alert.variant_info}</span>
                <Plus className="w-3 h-3" />
              </button>
            ))}
            {alerts.total_alerts > 8 && (
              <span className="text-sm text-orange-600">+{alerts.total_alerts - 8} more</span>
            )}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-charcoal-light" />
          <input
            type="text"
            placeholder="Search products..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-charcoal/20 bg-white text-charcoal focus:border-gold focus:outline-none"
          />
        </div>
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="px-4 py-2 border border-charcoal/20 bg-white text-charcoal focus:border-gold focus:outline-none"
        >
          <option value="all">All Products</option>
          <option value="out_of_stock">Out of Stock</option>
          <option value="low_stock">Low Stock</option>
          <option value="in_stock">In Stock</option>
        </select>
      </div>

      {/* Products List */}
      <div className="bg-white border border-charcoal/10">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-charcoal/10 text-xs text-charcoal-light uppercase tracking-wider">
                <th className="p-3 text-left">Product</th>
                <th className="p-3 text-center">Variants</th>
                <th className="p-3 text-center">Total Stock</th>
                <th className="p-3 text-right">Stock Value</th>
                <th className="p-3 text-center">Status</th>
                <th className="p-3 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-charcoal/5">
              {filteredProducts.map((product) => (
                <tr key={product.id} className="hover:bg-beige/30">
                  <td className="p-3">
                    <div className="flex items-center gap-3">
                      <img
                        src={product.image || ''}
                        alt=""
                        className="w-12 h-12 object-cover bg-beige"
                      />
                      <div>
                        <p className="text-sm font-medium text-charcoal">{product.name}</p>
                        <p className="text-xs text-charcoal-light">{product.category}</p>
                      </div>
                    </div>
                  </td>
                  <td className="p-3 text-center text-sm text-charcoal">
                    {product.variants_count}
                  </td>
                  <td className="p-3 text-center">
                    <span className={`text-lg font-heading ${
                      product.total_stock === 0 ? 'text-red-600' :
                      product.total_stock <= 10 ? 'text-orange-600' : 'text-charcoal'
                    }`}>
                      {product.total_stock}
                    </span>
                  </td>
                  <td className="p-3 text-right text-sm text-gold font-medium">
                    {formatCurrency(product.stock_value)}
                  </td>
                  <td className="p-3 text-center">
                    <div className="flex flex-col items-center gap-1">
                      {product.out_of_stock > 0 && (
                        <span className="px-2 py-0.5 text-xs bg-red-100 text-red-700">
                          {product.out_of_stock} Out
                        </span>
                      )}
                      {product.low_stock > 0 && (
                        <span className="px-2 py-0.5 text-xs bg-orange-100 text-orange-700">
                          {product.low_stock} Low
                        </span>
                      )}
                      {product.out_of_stock === 0 && product.low_stock === 0 && (
                        <span className="px-2 py-0.5 text-xs bg-green-100 text-green-700">
                          <CheckCircle className="w-3 h-3 inline mr-1" />
                          OK
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="p-3 text-center">
                    <button
                      onClick={() => openAdjustModal(product)}
                      className="px-3 py-1 text-sm text-gold hover:bg-gold/10 transition-colors"
                    >
                      <Edit2 className="w-4 h-4 inline mr-1" />
                      Adjust
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {filteredProducts.length === 0 && (
          <div className="p-8 text-center text-charcoal-light">
            No products found matching your criteria
          </div>
        )}
      </div>

      {/* Stock Adjustment Modal */}
      {showAdjustModal && selectedProduct && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-ivory w-full max-w-md">
            <div className="p-4 border-b border-charcoal/10">
              <h2 className="text-lg font-heading text-charcoal">Adjust Stock</h2>
              <p className="text-sm text-charcoal-light">{selectedProduct.name}</p>
            </div>
            
            <div className="p-4 space-y-4">
              {/* Variant Selection */}
              <div>
                <label className="block text-sm font-medium text-charcoal mb-2">Select Variant</label>
                <select
                  value={adjustmentData.variant_index}
                  onChange={(e) => setAdjustmentData({ ...adjustmentData, variant_index: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 border border-charcoal/20 bg-white text-charcoal focus:border-gold focus:outline-none"
                >
                  {selectedProduct.variants?.map((v, idx) => (
                    <option key={idx} value={idx}>
                      {v.size} / {v.color} - Current: {v.stock}
                    </option>
                  ))}
                </select>
              </div>

              {/* Action Type */}
              <div>
                <label className="block text-sm font-medium text-charcoal mb-2">Action</label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { value: 'add', label: 'Add Stock', icon: Plus },
                    { value: 'remove', label: 'Remove', icon: Minus },
                    { value: 'set', label: 'Set To', icon: Edit2 }
                  ].map((action) => (
                    <button
                      key={action.value}
                      type="button"
                      onClick={() => setAdjustmentData({ ...adjustmentData, action: action.value })}
                      className={`flex items-center justify-center gap-1 px-3 py-2 border ${
                        adjustmentData.action === action.value
                          ? 'border-gold bg-gold/10 text-gold'
                          : 'border-charcoal/20 text-charcoal-light hover:border-gold/30'
                      }`}
                    >
                      <action.icon className="w-4 h-4" />
                      <span className="text-sm">{action.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Quantity */}
              <div>
                <label className="block text-sm font-medium text-charcoal mb-2">Quantity</label>
                <input
                  type="number"
                  value={adjustmentData.quantity}
                  onChange={(e) => setAdjustmentData({ ...adjustmentData, quantity: parseInt(e.target.value) || 0 })}
                  min="0"
                  className="w-full px-3 py-2 border border-charcoal/20 bg-white text-charcoal focus:border-gold focus:outline-none"
                />
              </div>

              {/* Reason */}
              <div>
                <label className="block text-sm font-medium text-charcoal mb-2">Reason *</label>
                <select
                  value={adjustmentData.reason}
                  onChange={(e) => setAdjustmentData({ ...adjustmentData, reason: e.target.value })}
                  className="w-full px-3 py-2 border border-charcoal/20 bg-white text-charcoal focus:border-gold focus:outline-none"
                >
                  <option value="">Select reason</option>
                  <option value="restock">Restock / New Inventory</option>
                  <option value="return">Customer Return</option>
                  <option value="damaged">Damaged / Defective</option>
                  <option value="lost">Lost / Missing</option>
                  <option value="correction">Stock Correction</option>
                  <option value="other">Other</option>
                </select>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-charcoal mb-2">Notes (Optional)</label>
                <textarea
                  value={adjustmentData.notes}
                  onChange={(e) => setAdjustmentData({ ...adjustmentData, notes: e.target.value })}
                  rows={2}
                  placeholder="Additional notes..."
                  className="w-full px-3 py-2 border border-charcoal/20 bg-white text-charcoal focus:border-gold focus:outline-none"
                />
              </div>
            </div>

            <div className="p-4 border-t border-charcoal/10 flex gap-3">
              <button
                onClick={() => { setShowAdjustModal(false); setSelectedProduct(null); }}
                className="flex-1 px-4 py-2 border border-charcoal/20 text-charcoal hover:bg-beige"
              >
                Cancel
              </button>
              <button
                onClick={handleAdjustStock}
                className="flex-1 px-4 py-2 bg-gold text-white hover:bg-gold-dark"
              >
                Save Adjustment
              </button>
            </div>
          </div>
        </div>
      )}

      {/* History Modal */}
      {showHistoryModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-ivory w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b border-charcoal/10 flex items-center justify-between">
              <h2 className="text-lg font-heading text-charcoal">Stock Adjustment History</h2>
              <button
                onClick={() => setShowHistoryModal(false)}
                className="p-1 hover:bg-beige"
              >
                <XCircle className="w-5 h-5 text-charcoal-light" />
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto">
              {history.length > 0 ? (
                <div className="divide-y divide-charcoal/5">
                  {history.map((entry) => (
                    <div key={entry.id} className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <p className="font-medium text-charcoal">{entry.product_name}</p>
                          <p className="text-sm text-charcoal-light">{entry.variant_info}</p>
                        </div>
                        <span className={`px-2 py-0.5 text-xs ${
                          entry.action === 'add' ? 'bg-green-100 text-green-700' :
                          entry.action === 'remove' ? 'bg-red-100 text-red-700' :
                          'bg-blue-100 text-blue-700'
                        }`}>
                          {entry.action === 'add' ? '+' : entry.action === 'remove' ? '-' : '='}{entry.quantity_change}
                        </span>
                      </div>
                      <div className="text-sm text-charcoal-light">
                        <p>
                          {entry.previous_stock} → {entry.new_stock} units
                          <span className="mx-2">•</span>
                          {entry.reason}
                        </p>
                        {entry.notes && <p className="italic mt-1">{entry.notes}</p>}
                        <p className="text-xs mt-1">
                          By {entry.adjusted_by} • {new Date(entry.created_at).toLocaleString('en-IN')}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-8 text-center text-charcoal-light">
                  No stock adjustments recorded yet
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminInventory;
